/**
 * The text rules of a search, defined once: the model of a text query (the
 * terms a person typed or picked), the fold that makes „Müller“ and „mueller“
 * the same, the word, and the matcher that decides which search units match
 * and what to highlight in them. Pure functions, no Vue — the command palette,
 * the highlight components and the query input all import this module, so no
 * component depends on another component's model.
 *
 * The rules exist a second time in Go (`backend/module/search/textquery`).
 * Both implementations run one fixture file,
 * `backend/module/search/textquery/testdata/vectors.json` (here:
 * `textQuery.vectors.test.ts`). Where the text of this file and a vector
 * differ, the vector decides — and both implementations change with the file.
 *
 * Every range is half-open `[start, end)` in UTF-16 code units of the
 * **original** text, what `String.prototype.slice` takes.
 *
 * The text form of a query — `parse`, `serialize`, `canonical`,
 * `isTextRepresentable` — lives in `textQueryParse.ts`, and `describeQuery`
 * with its label sets in `textQueryDescribe.ts`. Both are re-exported at the
 * end of this file, so there is one module to import from.
 */

// ── Model ────────────────────────────────────────────────────────────────

/** How a term takes part in a unit: required, excluded, or one of an any-of group. */
export type Occur = 'must' | 'mustNot' | 'should';

/** Where in the text a term has to occur. */
export type Match = 'contains' | 'startsWith' | 'endsWith' | 'phrase';

/** One term of a query, as the editor holds it. */
export interface TextQueryTerm {
  /** Identity inside the editor. Never sent to a server. */
  id: string;
  text: string;
  occur: Occur;
  match: Match;
  /** Default `false`: case does not matter. */
  caseSensitive?: boolean;
  /** Stays in the query, but takes no part in matching and is not sent. */
  disabled?: boolean;
  /**
   * Restricts the term to fields of the unit whose query key is this.
   * Absent means every field, keyed or not.
   */
  field?: string;
}

/** A query as it is persisted: every term with its id and `disabled` flag, in order. */
export interface TextQuery {
  v: 1;
  terms: TextQueryTerm[];
}

/** A term as the wire carries it: no id, `caseSensitive` always spelled out. */
export interface ExecutableTerm {
  text: string;
  occur: Occur;
  match: Match;
  caseSensitive: boolean;
  /** Present only when the term is restricted to one field. */
  field?: string;
}

/** A query as the wire carries it: the enabled terms only. */
export interface ExecutableTextQuery {
  v: 1;
  terms: ExecutableTerm[];
}

/** A half-open range `[start, end)` in UTF-16 code units of the original text. */
export type TextRange = [start: number, end: number];

// ── Limits ───────────────────────────────────────────────────────────────

/** Most terms in one query. Disabled terms count. */
export const MAX_TERMS = 16;

/** Most code points in one term, after trimming. */
export const MAX_TERM_RUNES = 80;

/** Fewest literal code points a wildcard term needs beside its star. */
export const MIN_WILDCARD_RUNES = 3;

/**
 * Fewest code points a typed word needs before a spelling suggestion is asked.
 * Shorter words are too ambiguous to correct.
 */
export const MIN_SUGGEST_RUNES = 4;

/** Most members of a document chain one search may take along. */
export const MAX_CHAIN_MEMBERS = 64;

/** Most leaves (single conditions) of one filter. */
export const MAX_FILTER_LEAVES = 32;

// ── White space ──────────────────────────────────────────────────────────

/**
 * The Unicode `White_Space` property, by code unit (all of it is in the BMP).
 * Not `\s`: that one counts U+FEFF and misses U+0085, and Go's
 * `unicode.IsSpace` is exactly `White_Space`.
 *
 * @internal Shared with `textQueryParse.ts` and `textQueryDescribe.ts`.
 */
export function isWhiteSpace(code: number): boolean {
  return (
    (code >= 0x09 && code <= 0x0d) ||
    code === 0x20 ||
    code === 0x85 ||
    code === 0xa0 ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200a) ||
    code === 0x2028 ||
    code === 0x2029 ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x3000
  );
}

/** Trims `White_Space`, never `String.prototype.trim` (which knows U+FEFF and not U+0085). @internal */
export function trimWhiteSpace(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && isWhiteSpace(text.charCodeAt(start))) start += 1;
  while (end > start && isWhiteSpace(text.charCodeAt(end - 1))) end -= 1;
  return text.slice(start, end);
}

/** Whether `text` holds any `White_Space`. @internal */
export function hasWhiteSpace(text: string): boolean {
  for (let i = 0; i < text.length; i += 1) {
    if (isWhiteSpace(text.charCodeAt(i))) return true;
  }
  return false;
}

// ── Fold ─────────────────────────────────────────────────────────────────

/** A text folded for matching, with the way back to the original text. */
export interface Folded {
  /** The folded text. */
  text: string;
  /**
   * For each UTF-16 unit of `text`: where the original cluster it came from
   * starts. A cluster is one code point plus the nonspacing marks (`\p{Mn}`)
   * behind it, so the `ue` of a spelled ü, and an emoji's two surrogate
   * units, share one.
   */
  from: number[];
  /** For each UTF-16 unit of `text`: where that original cluster ends (exclusive). */
  to: number[];
}

/**
 * Letters the fold spells out. A capital is listed for `caseSensitive` only —
 * otherwise lower case is applied first (the capital sharp s then becomes `ß`
 * and folds to `ss`); with `caseSensitive` it folds to `SS`.
 */
const SPELLED = new Map<string, string>([
  ['\u{e4}', 'ae'], // ä
  ['\u{f6}', 'oe'], // ö
  ['\u{fc}', 'ue'], // ü
  ['\u{df}', 'ss'], // ß
  ['\u{e6}', 'ae'], // æ
  ['\u{f8}', 'o'], // ø
  ['\u{c4}', 'Ae'], // Ä
  ['\u{d6}', 'Oe'], // Ö
  ['\u{dc}', 'Ue'], // Ü
  ['\u{1e9e}', 'SS'], // ẞ
  ['\u{c6}', 'Ae'], // Æ
  ['\u{d8}', 'O'], // Ø
]);

/** The combining diacritical marks the fold drops, and no other mark. */
const DROPPED_MARKS = /[\u{300}-\u{36f}]/gu;

const NONSPACING_MARK = /^\p{Mn}$/u;

/** What an unpaired surrogate becomes: Go's JSON decoder makes the same of it. */
const REPLACEMENT = '\u{fffd}';

/** For each BMP code point: 0 not asked yet, 1 it is a nonspacing mark, 2 it is not. */
const MARKS = new Uint8Array(0x10000);

/**
 * Whether a code point is a nonspacing mark. None sits below U+0300, which keeps
 * the common case cheap; the property is looked up once per BMP code point, not
 * once per occurrence.
 */
function isMark(code: number): boolean {
  if (code < 0x300) return false;
  if (code > 0xffff) return NONSPACING_MARK.test(String.fromCodePoint(code));
  let known = MARKS[code]!;
  if (!known) MARKS[code] = known = NONSPACING_MARK.test(String.fromCharCode(code)) ? 1 : 2;
  return known === 1;
}

/**
 * What a single code point (no marks behind it) folds to, which depends on
 * nothing else — remembered, as a text repeats its letters. Key: the code point,
 * doubled, plus one for `caseSensitive`. Emptied when it holds {@link MAX_POINTS}.
 */
const POINTS = new Map<number, string>();
const MAX_POINTS = 4096;

function foldCluster(cluster: string, caseSensitive: boolean): string {
  let out = '';
  for (const char of cluster.normalize('NFC')) {
    // Case per code point: no context rule (a final sigma) may apply, and a
    // capital İ becomes `i` plus a mark that the next step drops.
    for (const piece of caseSensitive ? char : char.toLowerCase()) {
      out += SPELLED.get(piece) ?? piece.normalize('NFD').replace(DROPPED_MARKS, '');
    }
  }
  return out;
}

/**
 * Folds text for matching, on both sides of a comparison:
 *
 * - split into clusters (one code point plus the nonspacing marks behind it);
 *   every folded unit maps back to its whole cluster, so a range never starts
 *   or ends between a base and its mark or inside a surrogate pair;
 * - a lone (unpaired) surrogate is U+FFFD, one unit, a cluster of its own — the
 *   marks behind it belong to it like to any other base. Go cannot hold it
 *   either: its JSON decoder makes the same of it, so both sides agree;
 * - each cluster in NFC, so a decomposed `u` + U+0308 equals `ü`;
 * - per code point: lower case (unless `caseSensitive`); `ä ö ü ß æ ø` spelled
 *   `ae oe ue ss ae o` (`caseSensitive`: a capital becomes `Ae`, `Oe`, `Ue`, …);
 *   every other letter in NFD with the marks U+0300–U+036F dropped (`é` → `e`).
 *   Any other mark stays.
 *
 * White space, hyphens, dots, slashes and emoji pass through unchanged.
 */
export function fold(text: string, options: { caseSensitive?: boolean } = {}): Folded {
  const caseSensitive = options.caseSensitive === true;
  const folded: Folded = { text: '', from: [], to: [] };
  let at = 0;
  while (at < text.length) {
    const code = text.charCodeAt(at);
    // Fast path: an ASCII character no mark can follow.
    if (
      code < 0x80 &&
      (at + 1 === text.length ||
        text.charCodeAt(at + 1) < 0x300 ||
        !isMark(text.codePointAt(at + 1)!))
    ) {
      const upper = code >= 0x41 && code <= 0x5a;
      folded.text += upper && !caseSensitive ? String.fromCharCode(code + 0x20) : text[at];
      folded.from.push(at);
      folded.to.push(at + 1);
      at += 1;
      continue;
    }
    const point = text.codePointAt(at)!;
    // `codePointAt` gives a surrogate unit itself only where it has no partner.
    const lone = point >= 0xd800 && point <= 0xdfff;
    let end = at + (point > 0xffff ? 2 : 1);
    while (end < text.length) {
      const next = text.codePointAt(end)!;
      if (!isMark(next)) break;
      end += next > 0xffff ? 2 : 1;
    }
    let chunk: string;
    if (!lone && end === at + (point > 0xffff ? 2 : 1)) {
      const key = point * 2 + (caseSensitive ? 1 : 0);
      let known = POINTS.get(key);
      if (known === undefined) {
        if (POINTS.size >= MAX_POINTS) POINTS.clear();
        POINTS.set(key, (known = foldCluster(text.slice(at, end), caseSensitive)));
      }
      chunk = known;
    } else {
      const cluster = lone ? REPLACEMENT + text.slice(at + 1, end) : text.slice(at, end);
      chunk = foldCluster(cluster, caseSensitive);
    }
    folded.text += chunk;
    for (let i = 0; i < chunk.length; i += 1) {
      folded.from.push(at);
      folded.to.push(end);
    }
    at = end;
  }
  return folded;
}

// ── Words ────────────────────────────────────────────────────────────────

/** A word: a range of the text it was found in. */
export interface Word {
  start: number;
  end: number;
}

/**
 * The runs of units that are not white space in an already folded text, flat:
 * `[start0, end0, start1, end1, …]` as offsets into that folded text.
 * {@link words} and the matcher's word edges both come from here, so a word is
 * the same thing in both.
 */
function wordBoundsOf(folded: string): number[] {
  const out: number[] = [];
  let start = -1;
  for (let i = 0; i < folded.length; i += 1) {
    if (isWhiteSpace(folded.charCodeAt(i))) {
      if (start >= 0) out.push(start, i);
      start = -1;
    } else if (start < 0) {
      start = i;
    }
  }
  if (start >= 0) out.push(start, folded.length);
  return out;
}

/**
 * The words of a text: maximal runs of characters that are not Unicode white
 * space. Hyphens, dots and slashes belong to the word (`DI-EPDM-0009` is one
 * word), and so does punctuation (`Flachdichtung,` is one word, with its comma).
 *
 * The boundaries come from the **folded** text, mapped back through the fold's
 * `from` / `to` (case does not matter for white space): a nonspacing mark right
 * behind white space shares its cluster, folds away with it and never starts a
 * word, so `a`, a space, U+0301, `b` has its second word at `[3, 4)`.
 * Offsets are UTF-16 units of `text`.
 */
export function words(text: string): Word[] {
  const folded = fold(text);
  const bounds = wordBoundsOf(folded.text);
  const out: Word[] = [];
  for (let i = 0; i < bounds.length; i += 2) {
    out.push({ start: folded.from[bounds[i]!]!, end: folded.to[bounds[i + 1]! - 1]! });
  }
  return out;
}

// ── Matching ─────────────────────────────────────────────────────────────

/**
 * A text folded for matching, with what is derived from it built on demand: the
 * white-space-collapsed form (the text a phrase looks in) and the words (what
 * `startsWith` and `endsWith` look at).
 */
interface Hay {
  folded: Folded;
  collapsed?: Folded;
  /** The words of `folded.text`, flat (see {@link wordBoundsOf}). */
  wordBounds?: number[];
}

/** Collapses every run of white space to one space; that space maps to the whole run. */
function collapseWhiteSpace(folded: Folded): Folded {
  const out: Folded = { text: '', from: [], to: [] };
  let inRun = false;
  for (let i = 0; i < folded.text.length; i += 1) {
    const to = folded.to[i]!;
    if (isWhiteSpace(folded.text.charCodeAt(i))) {
      if (inRun) {
        out.to[out.to.length - 1] = to;
        continue;
      }
      out.text += ' ';
      inRun = true;
    } else {
      out.text += folded.text[i];
      inRun = false;
    }
    out.from.push(folded.from[i]!);
    out.to.push(to);
  }
  return out;
}

/** The folded text of a term; empty when nothing is left to look for. */
function needleOf(term: Pick<TextQueryTerm, 'text' | 'match' | 'caseSensitive'>): string {
  const folded = fold(trimWhiteSpace(term.text), { caseSensitive: term.caseSensitive === true });
  return term.match === 'phrase' ? collapseWhiteSpace(folded).text : folded.text;
}

// Ranges are kept flat while they are made and merged — `[start0, end0, start1,
// end1, …]`, sorted, nothing overlapping or touching — and become `[start, end]`
// pairs only in the result. No pair is made for a hit that is merged away, so a
// text with a hit at every position costs a few numbers, not one array per hit.

/** Adds `[start, end)` behind the last range of `out`, or into it when they overlap or touch. */
function emit(out: number[], start: number, end: number): void {
  const last = out.length - 1;
  if (last >= 0 && start <= out[last]!) {
    if (end > out[last]!) out[last] = end;
  } else {
    out.push(start, end);
  }
}

/** Merges two sorted, merged flat lists into a new one, in one pass. Never changes either. */
function mergeSorted(a: readonly number[], b: readonly number[]): number[] {
  if (!b.length) return a as number[];
  if (!a.length) return b as number[];
  const out: number[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (j >= b.length || (i < a.length && a[i]! <= b[j]!)) {
      emit(out, a[i]!, a[i + 1]!);
      i += 2;
    } else {
      emit(out, b[j]!, b[j + 1]!);
      j += 2;
    }
  }
  return out;
}

/** The flat ranges as `[start, end]` pairs: new arrays, shared with nothing. */
function pairsOf(flat: readonly number[]): TextRange[] {
  const out: TextRange[] = [];
  for (let i = 0; i < flat.length; i += 2) out.push([flat[i]!, flat[i + 1]!]);
  return out;
}

/** Whether `needle` stands in `text` at `at`: unit by unit, without a call into the runtime. */
function occursAt(text: string, needle: string, at: number): boolean {
  const length = needle.length;
  if (at < 0 || at + length > text.length) return false;
  for (let i = 0; i < length; i += 1) {
    if (text.charCodeAt(at + i) !== needle.charCodeAt(i)) return false;
  }
  return true;
}

/** The hits of `needle` at the start (or the end) of a word: one per word, never across one. */
function scanWords(hay: Hay, needle: string, atStart: boolean, out: number[] | undefined): boolean {
  const { text, from, to } = hay.folded;
  const bounds = (hay.wordBounds ??= wordBoundsOf(text));
  const length = needle.length;
  let found = false;
  for (let i = 0; i < bounds.length; i += 2) {
    const start = bounds[i]!;
    const end = bounds[i + 1]!;
    if (end - start < length) continue;
    const at = atStart ? start : end - length;
    if (!occursAt(text, needle, at)) continue;
    if (!out) return true;
    found = true;
    emit(out, from[at]!, to[at + length - 1]!);
  }
  return found;
}

/**
 * Looks for one prepared term in one text. Without `out` it only says whether
 * there is a hit, and stops at the first one. With `out` it finds every hit and
 * adds it to `out` as a range of the original text (see {@link emit}): left to
 * right they are sorted already, and hits that touch or overlap merge on the
 * spot — a run of back-to-back occurrences of the needle is one range, found by
 * comparing units, not by one search per occurrence.
 */
function scan(hay: Hay, needle: string, match: Match, out?: number[]): boolean {
  if (!needle) return false;
  let source: Folded;
  switch (match) {
    case 'contains':
      source = hay.folded;
      break;
    case 'phrase':
      source = hay.collapsed ??= collapseWhiteSpace(hay.folded);
      break;
    case 'startsWith':
    case 'endsWith':
      return scanWords(hay, needle, match === 'startsWith', out);
    default:
      return false;
  }
  const { text, from, to } = source;
  let at = text.indexOf(needle);
  if (at < 0) return false;
  if (!out) return true;
  const length = needle.length;
  do {
    let end = at + length;
    // The next occurrence would start right here and touch this one.
    while (occursAt(text, needle, end)) end += length;
    emit(out, from[at]!, to[end - 1]!);
    at = text.indexOf(needle, end);
  } while (at >= 0);
  return true;
}

/**
 * Every hit of one term in a text, as ranges of the original text: sorted,
 * overlapping and touching hits merged. Both sides are folded (`caseSensitive`
 * keeps case), and the term text is trimmed first. A term with nothing left to
 * look for — empty, white space only, a lone mark — finds nothing.
 *
 * - `contains`: every occurrence, left to right, none overlapping.
 * - `startsWith` / `endsWith`: per word (see {@link words}) of the folded text,
 *   never across one.
 * - `phrase`: runs of white space in term and text count as one space, then
 *   like `contains`.
 */
export function matchTerm(
  text: string,
  term: Pick<TextQueryTerm, 'text' | 'match' | 'caseSensitive'>
): TextRange[] {
  const hay: Hay = { folded: fold(text, { caseSensitive: term.caseSensitive === true }) };
  const found: number[] = [];
  scan(hay, needleOf(term), term.match, found);
  return pairsOf(found);
}

/** What {@link matchUnit} found. */
export interface UnitMatch {
  /** Whether the unit satisfies the query. */
  matched: boolean;
  /**
   * One list per field, in the order of the fields: the ranges to highlight,
   * from the matching `must` and `should` terms only (an excluded term is never
   * marked). Empty lists for a unit that does not match.
   */
  ranges: TextRange[][];
}

/** One search of a unit: terms with the same folded needle, match mode, case and field share it. */
interface Search {
  needle: string;
  match: Match;
  caseSensitive: boolean;
  /** The query key the term is restricted to; empty means every field. */
  field: string;
  /** Whether the needle occurs in some field it applies to; known once asked. */
  found?: boolean;
  /** Whether its ranges are going to be marked. */
  marked?: boolean;
}

/** The term's field key, trimmed the way validation trims it. Empty means every field. */
function termField(term: Pick<TextQueryTerm, 'field'>): string {
  return typeof term.field === 'string' ? trimWhiteSpace(term.field) : '';
}

/** Whether a term with `field` (empty = every field) looks at field `index`. */
function fieldApplies(field: string, index: number, keys: readonly string[] | undefined): boolean {
  if (!field) return true;
  return (keys?.[index] ?? '') === field;
}

/**
 * Evaluates a query on one search unit — the fields of one line, say its title
 * and description:
 *
 * - every enabled `must` term matches in some field;
 * - an enabled `mustNot` term matching in any field excludes the whole unit;
 * - if there are enabled `should` terms, at least one of them matches;
 * - disabled terms are ignored, and a query without an enabled term matches
 *   every unit, without ranges.
 *
 * A term never matches across two fields. The ranges of one field are sorted,
 * with overlapping and touching ranges of different terms merged.
 *
 * `keys[i]` is the query key of `fields[i]`; `''` is a field no fielded term
 * reaches. Omit `keys` and every field is unkeyed. A term with a `field` is
 * looked for only in fields with that key, and a fielded `mustNot` excludes
 * only on a hit there. A term without one still looks at every field.
 *
 * It works in two steps so that a text with a hit at every position stays
 * cheap, however many terms look at it. First the decision: for each term it
 * only asks whether the needle occurs, which ends at the first hit — a unit is
 * out as soon as a `must` term is missing or a `mustNot` term is there, and
 * nothing is marked then. Only a unit that matches gets its ranges, and those
 * are merged one term after the other into one sorted list per field. Terms that
 * look for the same needle in the same way, in the same field, are searched once.
 */
export function matchUnit(
  fields: readonly string[],
  terms: readonly TextQueryTerm[],
  keys?: readonly string[]
): UnitMatch {
  if (keys && keys.length !== fields.length) {
    throw new Error(`textquery: matchUnit: ${keys.length} keys for ${fields.length} fields`);
  }
  const none = (): TextRange[][] => fields.map(() => []);
  const enabled = terms.filter((term) => !term.disabled);
  if (!enabled.length) return { matched: true, ranges: none() };

  // Every field is folded once per case mode, and only when a term asks for it.
  const hays: Hay[][] = [[], []];
  const hayOf = (field: number, caseSensitive: boolean): Hay =>
    (hays[caseSensitive ? 1 : 0]![field] ??= { folded: fold(fields[field]!, { caseSensitive }) });

  const searches = new Map<string, Search>();
  const searchOf = (term: TextQueryTerm): Search => {
    const caseSensitive = term.caseSensitive === true;
    const needle = needleOf(term);
    const field = termField(term);
    const key = `${caseSensitive ? 1 : 0}${term.match}:${field}:${needle}`;
    let search = searches.get(key);
    if (!search) searches.set(key, (search = { needle, match: term.match, caseSensitive, field }));
    return search;
  };

  const marked: Search[] = [];
  let hasShould = false;
  let shouldMet = false;
  for (const term of enabled) {
    const search = searchOf(term);
    search.found ??= fields.some(
      (_, field) =>
        fieldApplies(search.field, field, keys) &&
        scan(hayOf(field, search.caseSensitive), search.needle, search.match)
    );
    if (term.occur === 'mustNot') {
      if (search.found) return { matched: false, ranges: none() };
      continue;
    }
    if (term.occur === 'must') {
      if (!search.found) return { matched: false, ranges: none() };
    } else if (term.occur === 'should') {
      hasShould = true;
      if (!search.found) continue;
      shouldMet = true;
    } else {
      continue;
    }
    if (!search.marked) {
      search.marked = true;
      marked.push(search);
    }
  }
  if (hasShould && !shouldMet) return { matched: false, ranges: none() };

  const ranges = fields.map((): number[] => []);
  for (const search of marked) {
    fields.forEach((_, field) => {
      if (!fieldApplies(search.field, field, keys)) return;
      const hits: number[] = [];
      scan(hayOf(field, search.caseSensitive), search.needle, search.match, hits);
      ranges[field] = mergeSorted(ranges[field]!, hits);
    });
  }
  return { matched: true, ranges: ranges.map(pairsOf) };
}

// ── Wire form and validation ─────────────────────────────────────────────

/**
 * The executable form of a query, what goes over the wire: the enabled terms
 * only, in order, without their ids and with `caseSensitive` always set. The
 * text stays as typed — the server trims and folds it. `field` is kept, trimmed,
 * and only when the term names one.
 */
export function toExecutable(terms: readonly TextQueryTerm[]): ExecutableTextQuery {
  return {
    v: 1,
    terms: terms
      .filter((term) => !term.disabled)
      .map((term) => {
        const executable: ExecutableTerm = {
          text: term.text,
          occur: term.occur,
          match: term.match,
          caseSensitive: term.caseSensitive === true,
        };
        const field = termField(term);
        if (field) executable.field = field;
        return executable;
      }),
  };
}

/** Why a query is not valid, as the `error` codes of the golden vectors name it. */
export type TextQueryError =
  | 'version'
  | 'too_many_terms'
  | 'term_empty'
  | 'term_too_long'
  | 'term_whitespace'
  | 'occur_invalid'
  | 'match_invalid'
  | 'field_invalid';

const OCCURS: readonly unknown[] = ['must', 'mustNot', 'should'] satisfies Occur[];
const MATCHES: readonly unknown[] = [
  'contains',
  'startsWith',
  'endsWith',
  'phrase',
] satisfies Match[];

/** A query key: a letter, then letters, digits and underscores. The same expression as Go. */
const FIELD_KEY = /^[A-Za-z][A-Za-z0-9_]*$/;

/** The number of code points of `text`, a lone surrogate counting as one. @internal */
export function countCodePoints(text: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) i += 1;
    }
    count += 1;
  }
  return count;
}

/** A decimal digit (`\p{Nd}`). Other numbers (superscripts, roman numerals) are not digits. */
const SUGGEST_DIGIT = /\p{Nd}/u;

/**
 * Whether `text` may be offered a spelling suggestion.
 *
 * Eligible means all of: one word (no Unicode white space anywhere — the text
 * is not trimmed, so surrounding space refuses it), not case-sensitive, no
 * wildcard `*` or `?`, at least {@link MIN_SUGGEST_RUNES} code points, and no
 * decimal digit. The server checks the same rules again; the client uses this
 * to decide whether to ask.
 */
export function isSuggestionEligible(text: string, caseSensitive = false): boolean {
  if (caseSensitive) return false;
  if (hasWhiteSpace(text)) return false;
  if (text.includes('*') || text.includes('?')) return false;
  if (SUGGEST_DIGIT.test(text)) return false;
  return countCodePoints(text) >= MIN_SUGGEST_RUNES;
}

/**
 * Checks a query against the wire rules and names the first violation, or
 * returns `null` for a valid query. Checked in this order: the version, the
 * number of terms (disabled terms count), then term by term — occur, match,
 * empty (trimmed, after folding), too long (more than {@link MAX_TERM_RUNES}
 * code points of the trimmed text), white space inside a term that is not a
 * phrase, then the field. An empty term list is valid, and so is a short
 * `startsWith` term: a wildcard's minimum is a rule of the text parser, not of
 * the wire.
 *
 * Without `allowedFields` a term that names a field is `field_invalid` — the
 * door with no list, the same one `Compile` is. With the option, the field has
 * to be one of those keys (a letter, then letters, digits and underscores,
 * after trimming). A blank field, or one that is not a string, is every field.
 *
 * The query may come from JSON nobody checked: something that is no object, or
 * whose `terms` are missing, `null` or no array, is not a v1 query (`version`),
 * and a term that is no object has no valid occur.
 *
 * Violations are reported, never trimmed away. Disabled terms are checked like
 * the others.
 */
export function validate(
  query: {
    v?: unknown;
    terms: readonly Partial<TextQueryTerm>[];
  },
  options?: { allowedFields?: readonly string[] }
): TextQueryError | null {
  if (typeof query !== 'object' || query === null) return 'version';
  if (query.v !== 1 || !Array.isArray(query.terms)) return 'version';
  if (query.terms.length > MAX_TERMS) return 'too_many_terms';
  const haveAllowed = options?.allowedFields != null;
  for (const term of query.terms as readonly (Partial<TextQueryTerm> | null)[]) {
    if (typeof term !== 'object' || term === null) return 'occur_invalid';
    if (!OCCURS.includes(term.occur)) return 'occur_invalid';
    if (!MATCHES.includes(term.match)) return 'match_invalid';
    const text = trimWhiteSpace(typeof term.text === 'string' ? term.text : '');
    if (!fold(text, { caseSensitive: term.caseSensitive === true }).text) return 'term_empty';
    if (countCodePoints(text) > MAX_TERM_RUNES) return 'term_too_long';
    if (term.match !== 'phrase' && hasWhiteSpace(text)) return 'term_whitespace';
    if (fieldInvalid(term.field, haveAllowed, options?.allowedFields)) return 'field_invalid';
  }
  return null;
}

/** Whether `field` is a key this door does not accept. A blank field is every field. */
function fieldInvalid(
  field: unknown,
  haveAllowed: boolean,
  allowed: readonly string[] | undefined
): boolean {
  if (typeof field !== 'string') return false;
  const key = trimWhiteSpace(field);
  if (!key) return false;
  if (!FIELD_KEY.test(key)) return true;
  return !haveAllowed || !allowed!.includes(key);
}

// ── The text form and the prose ──────────────────────────────────────────
// Both modules import from this one, and are re-exported from it: the
// functions they use are declarations, called long after every module has
// been evaluated, so the cycle is harmless.

export * from './textQueryParse';
export * from './textQueryDescribe';
